import { useState } from "react";
import { ChevronLeft, ChevronRight, ImagePlus, Images, Loader2, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, resolveImage } from "@/lib/api";
import MediaLibraryPicker from "@/components/MediaLibraryPicker";

const MAX = 12;

export default function ProductGalleryEditor({ images, mainUrl, onChange, onSetMain }) {
  const [progress, setProgress] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const list = images || [];

  const add = (urls) => {
    const next = [...list, ...urls.filter((u) => u && u !== mainUrl && !list.includes(u))].slice(0, MAX);
    if (list.length + urls.length > MAX) toast.error(`${MAX} photos supplémentaires maximum`);
    onChange(next);
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const urls = [];
    for (let i = 0; i < files.length; i += 1) {
      setProgress({ index: i + 1, total: files.length, pct: 0 });
      try {
        const fd = new FormData();
        fd.append("file", files[i]);
        fd.append("tag", "produits");
        const { data } = await api.post("/admin/upload", fd, {
          onUploadProgress: (ev) => ev.total && setProgress({ index: i + 1, total: files.length, pct: Math.round((ev.loaded / ev.total) * 100) }),
        });
        urls.push(data.url);
      } catch (err) {
        toast.error(`${files[i].name} : ${formatApiErrorDetail(err.response?.data?.detail)}`);
      }
    }
    setProgress(null);
    if (urls.length) {
      add(urls);
      toast.success(`${urls.length} photo(s) ajoutée(s) — pensez à enregistrer`);
    }
  };

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div data-testid="pf-gallery-section" className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
      <p className="text-sm font-medium text-bark">Autres photos <span className="text-stone-mute">({list.length}/{MAX})</span></p>
      <p className="mb-3 mt-1 text-xs text-stone-mute">Affichées en miniatures sous la photo principale.</p>
      {list.length > 0 && (
        <div className="mb-3 grid grid-cols-3 gap-2">
          {list.map((url, i) => (
            <div key={url} data-testid={`pf-gallery-item-${i}`} className="group relative overflow-hidden rounded-xl border border-amber-900/10">
              <img src={resolveImage(url)} alt="" className="aspect-square w-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 flex justify-between bg-bark/70 p-1 text-white">
                <button type="button" data-testid={`pf-gallery-left-${i}`} onClick={() => move(i, -1)} disabled={i === 0} aria-label="Déplacer à gauche" className="rounded p-0.5 hover:bg-white/20 disabled:opacity-30"><ChevronLeft size={14} /></button>
                <button type="button" data-testid={`pf-gallery-main-${i}`} onClick={() => onSetMain(i)} aria-label="Définir comme photo principale" title="Photo principale" className="rounded p-0.5 hover:bg-white/20"><Star size={14} /></button>
                <button type="button" data-testid={`pf-gallery-remove-${i}`} onClick={() => onChange(list.filter((_, k) => k !== i))} aria-label="Retirer" className="rounded p-0.5 hover:bg-white/20"><Trash2 size={14} /></button>
                <button type="button" data-testid={`pf-gallery-right-${i}`} onClick={() => move(i, 1)} disabled={i === list.length - 1} aria-label="Déplacer à droite" className="rounded p-0.5 hover:bg-white/20 disabled:opacity-30"><ChevronRight size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {progress && (
        <div data-testid="pf-gallery-progress" className="mb-3">
          <p className="mb-1 text-xs text-bark-soft">Photo {progress.index}/{progress.total} — {progress.pct}%</p>
          <div className="h-1.5 overflow-hidden rounded-full bg-sand"><div className="h-full bg-amber-deep transition-[width]" style={{ width: `${progress.pct}%` }} /></div>
        </div>
      )}
      <label data-testid="pf-gallery-upload-label" className={`flex cursor-pointer items-center justify-center gap-2 rounded-full border-2 border-amber-700/30 px-4 py-2.5 text-sm font-medium text-amber-900 transition-colors hover:border-amber-deep hover:bg-amber-50 ${progress || list.length >= MAX ? "pointer-events-none opacity-60" : ""}`}>
        {progress ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />}
        {progress ? "Téléversement…" : "Ajouter des photos"}
        <input data-testid="pf-gallery-upload-input" type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} disabled={Boolean(progress) || list.length >= MAX} />
      </label>
      <button type="button" data-testid="pf-gallery-library-btn" onClick={() => setPickerOpen(true)} disabled={list.length >= MAX}
        className="mt-2 flex w-full items-center justify-center gap-2 rounded-full border border-amber-900/15 px-4 py-2.5 text-sm font-medium text-bark-soft transition-colors hover:border-amber-deep hover:text-amber-deep disabled:opacity-50">
        <Images size={15} /> Ajouter depuis la bibliothèque
      </button>
      <MediaLibraryPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={(url) => add([url])} defaultTag="produits" />
    </div>
  );
}
